import React, { useState, useEffect, useRef, useCallback } from "react";

const MIN_HOURS = 1;

export default function HoursSelectModal({ onConfirm, initialValue }) {
  const safeInitial =
    initialValue && Number(initialValue) >= MIN_HOURS
      ? initialValue.toString()
      : MIN_HOURS.toString();
  const [selectedHour, setSelectedHour] = useState(safeInitial);
  const hourWheelRef = useRef(null);

  const hours = Array.from(
    { length: 24 - MIN_HOURS + 1 },
    (_, i) => (i + MIN_HOURS).toString(),
  );

  const setupWheel = useCallback((wheelRef, setSelected) => {
    const wheel = wheelRef.current;
    if (!wheel) return;

    const handleScroll = () => {
      const centerY = wheel.scrollTop + wheel.clientHeight / 2;
      let closest = null;
      let minDist = Infinity;

      const spans = wheel.querySelectorAll("span");
      spans.forEach((item) => {
        const itemCenter = item.offsetTop + item.offsetHeight / 2;
        const dist = Math.abs(centerY - itemCenter);
        if (dist < minDist) {
          closest = item;
          minDist = dist;
        }
      });

      if (closest) {
        if (closest.textContent !== wheel.dataset.lastSelected) {
          wheel.dataset.lastSelected = closest.textContent;
          setSelected(closest.textContent);
        }
      }
    };

    wheel.addEventListener("scroll", handleScroll, { passive: true });
    return () => wheel.removeEventListener("scroll", handleScroll);
  }, []);

  const handleItemClick = (wheelRef, value, setSelected) => {
    const wheel = wheelRef.current;
    if (!wheel) return;

    const spans = wheel.querySelectorAll("span");
    let targetSpan = null;
    spans.forEach((span) => {
      if (span.textContent === value) {
        targetSpan = span;
      }
    });

    if (targetSpan) {
      wheel.scrollTo({
        top:
          targetSpan.offsetTop -
          wheel.clientHeight / 2 +
          targetSpan.offsetHeight / 2,
        behavior: "smooth",
      });
      setSelected(value);
    }
  };

  useEffect(() => {
    setupWheel(hourWheelRef, setSelectedHour);
  }, [setupWheel]);

  useEffect(() => {
    if (initialValue) {
      const normalized =
        Number(initialValue) >= MIN_HOURS
          ? initialValue.toString()
          : MIN_HOURS.toString();
      setSelectedHour(normalized);
      // Small timeout to ensure the wheel is rendered before scrolling
      setTimeout(() => {
        handleItemClick(hourWheelRef, normalized, setSelectedHour);
      }, 300);
    }
  }, [initialValue]);

  const handleConfirm = () => {
    if (onConfirm) onConfirm(selectedHour);
  };

  return (
    <div
      className="modal moreInfo_modal fade"
      id="hoursSelectModal"
      tabIndex="-1"
      aria-labelledby="hoursSelectModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog moreInfo_modal-dialog hours_select_modal_dialog">
        <div className="modal-content moreInfo_modal-content text-white border-0 rounded-4">
          <div className="modal-header moreInfo_modal-header border-0 pb-0">
            <h1
              className="modal-title fs-5 fw-bold text-dark"
              id="hoursSelectModalLabel"
            >
              Select Hours
            </h1>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body moreInfo_modal-body pt-1 mt-2">
            <style>{`
              .hour-wheel::-webkit-scrollbar {
                display: none;
              }
              .hour-wheel {
                -ms-overflow-style: none;
                scrollbar-width: none;
              }
            `}</style>
            <div className="wheel-container d-flex align-items-center justify-content-center py-4 position-relative">
              <div
                className="hour-wheel text-end pe-4"
                ref={hourWheelRef}
                style={{ height: "150px", overflowY: "auto", width: "120px" }}
              >
                <div className="spacer" style={{ height: "40px" }}></div>
                {hours.map((h) => (
                  <span
                    key={h}
                    className={`d-block py-2 fs-3 ${h === selectedHour ? "active fw-bold text-dark" : "text-secondary opacity-50"}`}
                    onClick={() =>
                      handleItemClick(hourWheelRef, h, setSelectedHour)
                    }
                    style={{ cursor: "pointer", transition: "0.3s" }}
                  >
                    {h}
                  </span>
                ))}
                <div className="spacer" style={{ height: "40px" }}></div>
              </div>
              <span
                className="fs-4 text-secondary"
                style={{ marginLeft: "-10px" }}
              >
                Hours
              </span>
            </div>

            <div className="warning-box mt-5 bg-warning bg-opacity-75 text-dark p-3 rounded-3 mb-4 d-flex align-items-start">
              <span className="me-2 mt-1">
                <i className="bi bi-clock-fill"></i>
              </span>
              <div>
                <h6 className="fw-bold mb-1">
                  Extensions are subject to availability
                </h6>
                <p className="small mb-0">
                  We might not be able to extend your rental due to high demand.
                  Please plan accordingly.
                </p>
              </div>
            </div>
          </div>
          <div className="modal-footer moreInfo_modal-footer border-0 p-3">
            <button
              type="button"
              className="btn btn-outline-light w-100 py-3 rounded-pill fw-bold"
              data-bs-dismiss="modal"
              onClick={handleConfirm}
            >
              Book For {selectedHour} Hours
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
